import { useEffect, useState } from "react";
import { Plus, Trash2, Copy, Check, KeyRound } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";

const fmtDate = (iso, lang) => (iso ? new Date(iso).toLocaleString(lang === "zh" ? "zh-CN" : "en-US") : "—");

export const ApiTokensPanel = () => {
  const { t, lang } = useLanguage();
  const [tokens, setTokens] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [name, setName] = useState("");
  const [scope, setScope] = useState("fullaccess");
  const [saving, setSaving] = useState(false);
  const [reveal, setReveal] = useState(null); // { token, name, scope } — shown ONCE
  const [copied, setCopied] = useState(false);

  const fetchTokens = async () => {
    try {
      const { data } = await api.get("/auth/api-tokens");
      setTokens(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => { fetchTokens(); }, []);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/auth/api-tokens", { name, scope });
      setReveal({ token: data.token, name: data.name, scope: data.scope });
      setName("");
      setScope("fullaccess");
      setOpenCreate(false);
      fetchTokens();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (id, name) => {
    if (!window.confirm(t("apiTokens.revokeConfirm").replace("{name}", name))) return;
    try {
      await api.delete(`/auth/api-tokens/${id}`);
      toast.success(t("apiTokens.revoked"));
      fetchTokens();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleCopy = async () => {
    if (!reveal?.token) return;
    try {
      await navigator.clipboard.writeText(reveal.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-lg font-bold tracking-tight text-foreground flex items-center gap-2">
            <KeyRound size={18} /> {t("apiTokens.title")}
          </h2>
          <p className="text-xs text-muted-foreground mt-1">{t("apiTokens.subtitle")}</p>
        </div>
        <Dialog open={openCreate} onOpenChange={setOpenCreate}>
          <DialogTrigger asChild>
            <Button data-testid="create-api-token-button" className="rounded-md bg-brand hover:bg-brand-hover">
              <Plus size={16} /> {t("apiTokens.create")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-sm" data-testid="create-api-token-dialog">
            <DialogHeader><DialogTitle>{t("apiTokens.create")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>{t("apiTokens.name")}</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("apiTokens.namePlaceholder")}
                  data-testid="new-api-token-name-input"
                  maxLength={60}
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t("apiTokens.scope")}</Label>
                <div className="grid grid-cols-2 gap-2" data-testid="new-api-token-scope-radio">
                  {[
                    { v: "fullaccess", label: t("apiTokens.scope_fullaccess"), hint: t("apiTokens.scope_fullaccess_hint") },
                    { v: "readonly", label: t("apiTokens.scope_readonly"), hint: t("apiTokens.scope_readonly_hint") },
                  ].map(({ v, label, hint }) => (
                    <button
                      type="button"
                      key={v}
                      onClick={() => setScope(v)}
                      data-testid={`new-api-token-scope-${v}`}
                      className={`text-left rounded-md border p-2.5 transition-colors ${
                        scope === v ? "border-brand bg-brand/5" : "border-border hover:border-muted-foreground"
                      }`}
                    >
                      <div className="text-sm font-semibold">{label}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenCreate(false)} className="rounded-md">{t("common.cancel")}</Button>
              <Button onClick={handleCreate} disabled={saving || !name.trim()} data-testid="submit-new-api-token-button" className="rounded-md bg-brand hover:bg-brand-hover">
                {saving ? "..." : t("common.save")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* One-time reveal dialog */}
      <Dialog open={Boolean(reveal)} onOpenChange={(o) => !o && setReveal(null)}>
        <DialogContent className="max-w-lg" data-testid="reveal-api-token-dialog">
          <DialogHeader><DialogTitle>{t("apiTokens.revealTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-xs px-3 py-2">
              {t("apiTokens.revealWarning")}
            </div>
            <div className="space-y-1.5">
              <Label>{t("apiTokens.name")}</Label>
              <div className="text-sm font-medium">{reveal?.name}</div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("apiTokens.scope")}</Label>
              <Badge variant={reveal?.scope === "readonly" ? "secondary" : "default"} className={reveal?.scope === "readonly" ? "" : "bg-emerald-100 text-emerald-800 hover:bg-emerald-100"}>
                {reveal?.scope === "readonly" ? t("apiTokens.scope_readonly") : t("apiTokens.scope_fullaccess")}
              </Badge>
            </div>
            <div className="space-y-1.5">
              <Label>{t("apiTokens.token")}</Label>
              <div className="flex items-center gap-2">
                <code data-testid="reveal-api-token-value" className="flex-1 px-3 py-2 rounded-md bg-secondary font-mono text-xs break-all select-all">
                  {reveal?.token}
                </code>
                <Button onClick={handleCopy} variant="outline" className="rounded-md shrink-0" data-testid="copy-api-token-button">
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                </Button>
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              {t("apiTokens.usageHint")}
              <code className="mx-1 px-1 py-0.5 bg-secondary rounded text-[10px]">Authorization: Bearer {reveal?.token?.slice(0, 10)}…</code>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setReveal(null)} className="rounded-md bg-brand hover:bg-brand-hover" data-testid="close-reveal-api-token-button">
              {t("common.done")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="bg-card border border-border rounded-md overflow-hidden">
        <Table data-testid="api-tokens-table">
          <TableHeader>
            <TableRow>
              <TableHead>{t("apiTokens.name")}</TableHead>
              <TableHead>{t("apiTokens.scope")}</TableHead>
              <TableHead>{t("apiTokens.prefix")}</TableHead>
              <TableHead>{t("apiTokens.createdBy")}</TableHead>
              <TableHead>{t("apiTokens.createdAt")}</TableHead>
              <TableHead>{t("apiTokens.lastUsed")}</TableHead>
              <TableHead>{t("apiTokens.status")}</TableHead>
              <TableHead className="text-right">{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tokens.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground text-sm">
                  {t("apiTokens.empty")}
                </TableCell>
              </TableRow>
            )}
            {tokens.map((tk) => (
              <TableRow key={tk.id} data-testid={`api-token-row-${tk.id}`}>
                <TableCell className="font-medium">{tk.name}</TableCell>
                <TableCell>
                  <Badge variant={tk.scope === "readonly" ? "secondary" : "default"}
                    className={tk.scope === "readonly" ? "" : "bg-emerald-100 text-emerald-800 hover:bg-emerald-100"}
                    data-testid={`api-token-scope-${tk.id}`}>
                    {tk.scope === "readonly" ? t("apiTokens.scope_readonly") : t("apiTokens.scope_fullaccess")}
                  </Badge>
                </TableCell>
                <TableCell><code className="font-mono text-xs text-muted-foreground">{tk.prefix}…</code></TableCell>
                <TableCell className="text-xs">{tk.created_by_email}</TableCell>
                <TableCell className="text-xs">{fmtDate(tk.created_at, lang)}</TableCell>
                <TableCell className="text-xs">{fmtDate(tk.last_used_at, lang)}</TableCell>
                <TableCell>
                  {tk.revoked
                    ? <Badge variant="secondary" className="text-muted-foreground">{t("apiTokens.revoked")}</Badge>
                    : <Badge variant="default" className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">{t("apiTokens.active")}</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  {!tk.revoked && (
                    <Button
                      variant="ghost" size="icon"
                      onClick={() => handleRevoke(tk.id, tk.name)}
                      title={t("apiTokens.revoke")}
                      data-testid={`revoke-api-token-${tk.id}`}
                    >
                      <Trash2 size={16} className="text-destructive" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
